import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Card, Row, Col, Button, Tabs, message, Modal, Select } from 'antd';
import { FileTextOutlined, CarOutlined, CommentOutlined, TableOutlined, TeamOutlined, TruckOutlined, BankOutlined, ShopOutlined } from '@ant-design/icons';
import './ReportsCentre.css';
import api from '../../authentication/axiosInstance';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';

const ReportsCentre = () => {
  const [activeTab, setActiveTab] = useState('planning');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [territoryManagers, setTerritoryManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState(null);
  const [isStormModalVisible, setIsStormModalVisible] = useState(false);
  const [storms, setStorms] = useState([]);
  const [selectedStorm, setSelectedStorm] = useState(null);
  const navigate = useNavigate();

  // Fetch territory managers when component mounts
  useEffect(() => {
    fetchTerritoryManagers();
    fetchStorms();
  }, []);

  const fetchTerritoryManagers = async () => {
    try {
      // Get all sites with their assigned territory managers
      const sitesResponse = await api.get('/sites/');
      
      // Extract unique territory managers
      const uniqueManagers = [...new Set(
        sitesResponse.data
          .filter(site => site.assigned_territory_manager) // Filter out null/undefined managers
          .map(site => site.assigned_territory_manager)
      )];

      // Format for Select component
      const formattedManagers = uniqueManagers.map(name => ({
        name: name,
        staff_id: name // Using name as the value since that's what the backend expects
      }));

      setTerritoryManagers(formattedManagers);
    } catch (error) {
      console.error('Error fetching territory managers:', error);
      message.error('Failed to fetch territory managers');
    }
  };

  const fetchStorms = async () => {
    try {
      const response = await api.get('/dispatch/storm/');
      setStorms(response.data);
    } catch (error) {
      console.error('Error fetching storms:', error);
      message.error('Failed to fetch storms');
    }
  };

  const handleLoaderReport = async (reportType) => {
    try {
      let endpoint;
      let filename;
      
      switch (reportType) {
        case 'By Loader':
          endpoint = '/vehicles/loader_pdf/';
          filename = 'loader_allocation_report.pdf';
          break;
        case 'By Site':
          endpoint = '/vehicles/site_pdf/';
          filename = 'site_allocation_report.pdf';
          break;
        case 'Generic Allocation':
          endpoint = '/vehicles/generic_loader_pdf/';
          filename = 'generic_loader_report.pdf';
          break;
        default:
          return;
      }

      const response = await api.get(endpoint, {
        responseType: 'blob'
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      message.success('Report generated successfully');
    } catch (error) {
      console.error('Error generating report:', error);
      message.error('Failed to generate report');
    }
  };

  const handleWalkwayCrewReport = async (action) => {
    try {
      if (action === 'View Report') {
        const response = await api.get('/walkwayCrew/walkwaycrews/pdf/', {
          responseType: 'blob'
        });

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'walkway_crew_allocation_report.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
        message.success('Walkway Crew report generated successfully');
      }
    } catch (error) {
      console.error('Error generating walkway crew report:', error);
      message.error('Failed to generate report');
    }
  };

  const handleSaltingRouteReport = async (action) => {
    try {
      if (action === 'View Report') {
        const response = await api.get('/saltingRoute/saltingRoutes/pdf/', {
          responseType: 'blob'
        });

        // Create blob link to download
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'salting_route_allocation_report.pdf');
        document.body.appendChild(link);
        link.click();
        link.remove();
        message.success('Salting Route report generated successfully');
      }
    } catch (error) {
      console.error('Error generating salting route report:', error);
      message.error('Failed to generate report');
    }
  };

  const handleSiteManagementReport = async (action) => {
    try {
      switch (action) {
        case 'All Territory Managers':
          const response = await api.get('/sites/site_supervision_pdf/', {
            responseType: 'blob'
          });
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'site_supervision_report.pdf');
          document.body.appendChild(link);
          link.click();
          link.remove();
          message.success('Territory Managers report generated successfully');
          break;

        case 'Single Manager Report':
          setIsModalVisible(true);
          break;

        case 'Export CSV':
          const csvResponse = await api.get('/sites/export_csv/', {  // Changed to use the CSV export endpoint
            responseType: 'blob'
          });
          const csvUrl = window.URL.createObjectURL(new Blob([csvResponse.data]));
          const csvLink = document.createElement('a');
          csvLink.href = csvUrl;
          csvLink.setAttribute('download', 'sites_export.csv');
          document.body.appendChild(csvLink);
          csvLink.click();
          csvLink.remove();
          message.success('CSV exported successfully');
          break;
      }
    } catch (error) {
      console.error('Error with site management report:', error);
      message.error('Failed to generate report');
    }
  };

  const handleManagerSelect = async () => {
    if (!selectedManager) {
      message.error('Please select a territory manager');
      return;
    }

    try {
      const response = await api.get('/sites/site_supervision_pdf_for_manager/', {
        params: { territory_manager: selectedManager },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `territory_manager_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      setIsModalVisible(false);
      setSelectedManager(null);
      message.success('Territory Manager report generated successfully');
    } catch (error) {
      console.error('Error generating territory manager report:', error);
      message.error('Failed to generate report');
    }
  };

  const handleDispatchReport = async (action) => {
    try {
      switch (action) {
        case 'Storm Report':
          const response = await api.get('/dispatch/storm-report/', {
            responseType: 'blob'
          });
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `storm_report_all.pdf`;
          link.click();
          message.success('Storm report generated successfully');
          break;

        case 'By Storm ID':
          setIsStormModalVisible(true);
          break;
      }
    } catch (error) {
      console.error('Error generating dispatch report:', error);
      message.error('Failed to generate the storm report');
    }
  };

  const handleStormSelect = async () => {
    if (!selectedStorm) {
      message.error('Please select a storm');
      return;
    }

    try {
      const response = await api.get('/dispatch/single-storm-report/', {
        params: { storm_id: selectedStorm },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `storm_report_${selectedStorm}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setIsStormModalVisible(false);
      setSelectedStorm(null);
      message.success('Storm report generated successfully');
    } catch (error) {
      console.error('Error generating storm report:', error);
      message.error('Failed to generate report');
    }
  };

  const handleCommunicationReport = async (action) => {
    try {
      switch (action) {
        case 'Warning Report':
          const response = await api.get('/dispatch/message-report/', {
            params: { region: undefined },
            responseType: 'blob'
          });

          const blob = new Blob([response.data], { type: 'application/pdf' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `message_report_all.pdf`;
          link.click();
          message.success('Report downloaded successfully!');
          break;

        case 'Export CSV':        // Handle export data if needed
          break;
      }
    } catch (error) {
      console.error('Error generating communication report:', error);
      message.error('Failed to generate the report');
    }
  };

  const handleEquipmentReport = async (action) => {
    try {
      switch (action) {
        case 'Generate Report':
          const pdfResponse = await api.get('/equipment/equipment_export_pdf/', {
            responseType: 'blob'
          });
          const pdfUrl = window.URL.createObjectURL(new Blob([pdfResponse.data]));
          const pdfLink = document.createElement('a');
          pdfLink.href = pdfUrl;
          pdfLink.setAttribute('download', 'equipment_report.pdf');
          document.body.appendChild(pdfLink);
          pdfLink.click();
          pdfLink.remove();
          message.success('Equipment report generated successfully');
          break;

        case 'Export CSV':
          const csvResponse = await api.get('/equipment/equipment_export_csv/', {
            responseType: 'blob'
          });
          const csvUrl = window.URL.createObjectURL(new Blob([csvResponse.data]));
          const csvLink = document.createElement('a');
          csvLink.href = csvUrl;
          csvLink.setAttribute('download', 'equipment_export.csv');
          document.body.appendChild(csvLink);
          csvLink.click();
          csvLink.remove();
          message.success('Equipment CSV exported successfully');
          break;
      }
    } catch (error) {
      console.error('Error with equipment report:', error);
      message.error('Failed to generate report');
    }
  };

  const handleStaffReport = async (action) => {
    try {
      switch (action) {
        case 'Generate Report':
          const pdfResponse = await api.get('/staff/staff_export_pdf/', {
            responseType: 'blob'
          });
          const pdfUrl = window.URL.createObjectURL(new Blob([pdfResponse.data]));
          const pdfLink = document.createElement('a');
          pdfLink.href = pdfUrl;
          pdfLink.setAttribute('download', 'staff_report.pdf');
          document.body.appendChild(pdfLink);
          pdfLink.click();
          pdfLink.remove();
          message.success('Staff report generated successfully');
          break;

        case 'Export CSV':
          const csvResponse = await api.get('/staff/export_csv/', {
            responseType: 'blob'
          });
          const csvUrl = window.URL.createObjectURL(new Blob([csvResponse.data]));
          const csvLink = document.createElement('a');
          csvLink.href = csvUrl;
          csvLink.setAttribute('download', 'staff_export.csv');
          document.body.appendChild(csvLink);
          csvLink.click();
          csvLink.remove();
          message.success('Staff CSV exported successfully');
          break;
      }
    } catch (error) {
      console.error('Error with staff report:', error);
      message.error('Failed to generate report');
    }
  };

  const handleVehicleReport = async (action) => {
    try {
      switch (action) {
        case 'Generate Report':
          const pdfResponse = await api.get('/vehicles/vehicle_export_pdf/', {
            responseType: 'blob'
          });
          const pdfUrl = window.URL.createObjectURL(new Blob([pdfResponse.data]));
          const pdfLink = document.createElement('a');
          pdfLink.href = pdfUrl;
          pdfLink.setAttribute('download', 'vehicle_report.pdf');
          document.body.appendChild(pdfLink);
          pdfLink.click();
          pdfLink.remove();
          message.success('Vehicle report generated successfully');
          break;

        case 'Export CSV':
          const csvResponse = await api.get('/vehicles/vehicle_export_csv/', {
            responseType: 'blob'
          });
          const csvUrl = window.URL.createObjectURL(new Blob([csvResponse.data]));
          const csvLink = document.createElement('a');
          csvLink.href = csvUrl;
          csvLink.setAttribute('download', 'vehicle_export.csv');
          document.body.appendChild(csvLink);
          csvLink.click();
          csvLink.remove();
          message.success('Vehicle CSV exported successfully');
          break;
      }
    } catch (error) {
      console.error('Error with vehicle report:', error);
      message.error('Failed to generate report');
    }
  };

  const handleClientReport = async (action) => {
    try {
      switch (action) {
        case 'Generate Report':
          const pdfResponse = await api.get('/clients/client_export_pdf/', {
            responseType: 'blob'
          });
          const pdfUrl = window.URL.createObjectURL(new Blob([pdfResponse.data]));
          const pdfLink = document.createElement('a');
          pdfLink.href = pdfUrl;
          pdfLink.setAttribute('download', 'client_report.pdf');
          document.body.appendChild(pdfLink);
          pdfLink.click();
          pdfLink.remove();
          message.success('Client report generated successfully');
          break;

        case 'Export CSV':
          const csvResponse = await api.get('/clients/client_export_csv/', {
            responseType: 'blob'
          });
          const csvUrl = window.URL.createObjectURL(new Blob([csvResponse.data]));
          const csvLink = document.createElement('a');
          csvLink.href = csvUrl;
          csvLink.setAttribute('download', 'client_export.csv');
          document.body.appendChild(csvLink);
          csvLink.click();
          csvLink.remove();
          message.success('Client CSV exported successfully');
          break;
      }
    } catch (error) {
      console.error('Error with client report:', error);
      message.error('Failed to generate report');
    }
  };

  const planningReports = [
    {
      title: 'Loader Allocation',
      icon: <TruckOutlined style={{ fontSize: '24px' }} />,
      description: 'View and generate loader allocation reports by loader and site.',
      actions: [
        'By Loader',
        'By Site',
        'Generic Allocation'
      ]
    },
    {
      title: 'Walkway Crew Allocation',
      icon: <TeamOutlined style={{ fontSize: '24px' }} />,
      description: 'Current walkway crew allocation reports and statistics.',
      actions: ['View Report']
    },
    {
      title: 'Salting Route Allocation',
      icon: <TableOutlined style={{ fontSize: '24px' }} />,
      description: 'Current salting route allocation reports and assignments.',
      actions: ['View Report']
    },
    {
      title: 'Site Management',
      icon: <BankOutlined style={{ fontSize: '24px' }} />,
      description: 'Generate reports for territory managers and their assigned sites.',
      actions: [
        'All Territory Managers',
        'Single Manager Report',
        'Export CSV'
      ]
    }
  ];

  const executionReports = [
    {
      title: 'Dispatch Reports',
      icon: <FileTextOutlined style={{ fontSize: '24px' }} />,
      description: 'Storm reports and dispatch tracking by storm ID.',
      actions: ['Storm Report', 'By Storm ID']
    },
    {
      title: 'Communication Reports',
      icon: <CommentOutlined style={{ fontSize: '24px' }} />,
      description: 'Regional warning reports and storm communication logs.',
      actions: ['Warning Report']
    }
  ];

  const complianceReports = [
    {
      title: 'Equipment Reports',
      icon: <ShopOutlined style={{ fontSize: '24px' }} />,
      description: 'View and generate equipment reports.',
      actions: [
        'Generate Report',
        'Export CSV'
      ],
      handler: handleEquipmentReport
    },
    {
      title: 'Vehicle Reports',
      icon: <CarOutlined style={{ fontSize: '24px' }} />,
      description: 'View and generate vehicle reports.',
      actions: ['Generate Report', 'Export CSV'],
      handler: handleVehicleReport
    },
    {
      title: 'Staff Reports',
      icon: <TeamOutlined style={{ fontSize: '24px' }} />,
      description: 'View and generate staff reports.',
      actions: ['Generate Report', 'Export CSV'],
      handler: handleStaffReport
    },
    {
      title: 'Client Reports',
      icon: <BankOutlined style={{ fontSize: '24px' }} />,
      description: 'View and generate client reports.',
      actions: ['Generate Report', 'Export CSV'],
      handler: handleClientReport
    }
  ];

  const renderReportCards = (reports) => (
    <Row gutter={[16, 16]}>
      {reports.map((report, index) => (
        <Col xs={24} sm={12} md={8} key={index}>
          <Card
            title={
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '10px',
                fontFamily: '"Plus Jakarta Sans", sans-serif',
              }}>
                {report.icon}
                <span>{report.title}</span>
              </div>
            }
            style={{ 
              height: '100%',
              transition: 'all 0.3s ease',
              cursor: 'pointer',
              fontFamily: '"Plus Jakarta Sans", sans-serif',
              '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }
            }}
            hoverable
          >
            <p style={{ 
              marginBottom: 20,
              fontFamily: '"Plus Jakarta Sans", sans-serif'
            }}>
              {report.description}
            </p>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {report.actions.map((action, actionIndex) => (
                <Button
                  key={actionIndex}
                  type="primary"
                  className={action.toLowerCase().includes('export') ? 'report-button export-button' : 'report-button'}
                  style={{
                    padding: '4px 15px',
                    height: 'auto',
                    fontFamily: '"Plus Jakarta Sans", sans-serif'
                  }}
                  onClick={() => {
                    if (report.title === 'Loader Allocation') {
                      handleLoaderReport(action);
                    } else if (report.title === 'Walkway Crew Allocation') {
                      handleWalkwayCrewReport(action);
                    } else if (report.title === 'Salting Route Allocation') {
                      handleSaltingRouteReport(action);
                    } else if (report.title === 'Site Management') {
                      handleSiteManagementReport(action);
                    } else if (report.title === 'Dispatch Reports') {
                      handleDispatchReport(action);
                    } else if (report.title === 'Communication Reports') {
                      handleCommunicationReport(action);
                    } else if (report.title === 'Equipment Reports') {
                      report.handler(action);
                    } else if (report.title === 'Vehicle Reports') {
                      report.handler(action);
                    } else if (report.title === 'Staff Reports') {
                      report.handler(action);
                    } else if (report.title === 'Client Reports') {
                      report.handler(action);
                    }
                  }}
                >
                  {action}
                </Button>
              ))}
            </div>
          </Card>
        </Col>
      ))}
    </Row>
  );

  const items = [
    {
      key: 'planning',
      label: 'Resource Planning',
      children: renderReportCards(planningReports),
    },
    {
      key: 'execution',
      label: 'Operational Execution',
      children: renderReportCards(executionReports),
    },
    {
      key: 'compliance',
      label: 'Administrative',
      children: renderReportCards(complianceReports),
    }
  ];

  const tabStyle = {
    '.ant-tabs-nav': {
      marginBottom: '24px',
      fontFamily: '"Plus Jakarta Sans", sans-serif',
    },
    '.ant-tabs-tab': {
      padding: '12px 24px',
      fontSize: '18px',
      fontWeight: 500,
      marginRight: '32px',
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      color: '#666666',  // Default color
    },
    '.ant-tabs-tab:hover': {  // Changed to direct hover selector
      color: '#015544 !important',  // Light green
    },
    '.ant-tabs-tab-active': {
      fontWeight: 600,
      color: '#003d2f !important',  // Dark green for active tab
    },
    '.ant-tabs-ink-bar': {
      backgroundColor: '#003d2f !important',  // Dark green underline
      height: '3px',
    }
  };

  return (
    <Box style={{ padding: 30 }}>
      <Typography 
        variant="h4" 
        style={{ 
          marginBottom: 20, 
          fontWeight: 600,
          fontSize: '28px',
          fontFamily: '"Plus Jakarta Sans", sans-serif',
          color: '#000000'
        }}
      >
        Reports Centre
      </Typography>
      
      <Tabs
        defaultActiveKey="planning"
        items={items}
        onChange={setActiveTab}
        style={{ marginBottom: 20 }}
        size="large"
        styles={tabStyle}
      />
      
      <Modal
        title="Select Territory Manager"
        open={isModalVisible}
        onOk={handleManagerSelect}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedManager(null);
        }}
        okText="Generate Report"
        cancelText="Cancel"
        centered
        width={400}
        styles={{
          header: {
            paddingBottom: 20,
            borderBottom: '1px solid #f0f0f0',
            fontFamily: '"Plus Jakarta Sans", sans-serif'
          },
          body: {
            padding: '24px',
            fontFamily: '"Plus Jakarta Sans", sans-serif'
          },
          footer: {
            marginTop: 10,
            borderTop: '1px solid #f0f0f0',
            padding: '10px 24px',
            fontFamily: '"Plus Jakarta Sans", sans-serif'
          }
        }}
      >
        <Select
          style={{ width: '100%' }}
          placeholder="Select a territory manager"
          onChange={(value) => setSelectedManager(value)}
          value={selectedManager}
          size="large"
        >
          {territoryManagers.map((manager) => (
            <Select.Option key={manager.staff_id} value={manager.staff_id}>
              {manager.name}
            </Select.Option>
          ))}
        </Select>
      </Modal>

      <Modal
        title="Select Storm"
        open={isStormModalVisible}
        onOk={handleStormSelect}
        onCancel={() => {
          setIsStormModalVisible(false);
          setSelectedStorm(null);
        }}
        okText="Generate Report"
        cancelText="Cancel"
        centered
        width={500}
        styles={{
          header: {
            paddingBottom: 20,
            borderBottom: '1px solid #f0f0f0',
            fontFamily: '"Plus Jakarta Sans", sans-serif'
          },
          body: {
            padding: '24px',
            fontFamily: '"Plus Jakarta Sans", sans-serif'
          },
          footer: {
            marginTop: 10,
            borderTop: '1px solid #f0f0f0',
            padding: '10px 24px'
          }
        }}
      >
        <Select
          style={{ width: 400 }}
          placeholder="Select a storm"
          onChange={(value) => setSelectedStorm(value)}
          value={selectedStorm}
          size="large"
          showSearch
          filterOption={(input, option) =>
            option.children.toLowerCase().includes(input.toLowerCase())
          }
        >
          <Select.Option key="all" value="All">
            All Storms
          </Select.Option>
          {storms.map((storm) => (
            <Select.Option key={storm.storm_id} value={storm.storm_id}>
              {`Type: ${storm.storm_type} - ${moment(storm.date_of_storm).format('MMMM Do, YYYY')} - ID: ${storm.storm_id}`}
            </Select.Option>
          ))}
        </Select>
      </Modal>
    </Box>
  );
};

export default ReportsCentre; 